import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import styles from './ui.module.css'

/**
 * A confirmation before something the user cannot undo from DealPulse.
 *
 * Escape is handled in the capture phase and stopped there. The deal panel also
 * listens for Escape on the document, and without this the one key press would
 * both cancel the confirmation and close the panel behind it.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  pending = false,
  onConfirm,
  onCancel,
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  pending?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation()
        onCancel()
        return
      }
      // Tab is trapped here too. The panel behind traps Tab within itself, which
      // would otherwise let focus walk out of a modal question and onto the
      // controls it is asking about.
      if (event.key !== 'Tab') return
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])')
      if (!focusable || focusable.length === 0) return
      event.stopImmediatePropagation()
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const target = document.activeElement
      if (event.shiftKey && (target === first || !dialogRef.current?.contains(target))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (target === last || !dialogRef.current?.contains(target))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [onCancel])

  return (
    <div className={styles.confirmScrim}>
      <div className={styles.confirm} role="alertdialog" aria-modal="true" aria-label={title} ref={dialogRef}>
        <p className={styles.confirmTitle}>{title}</p>
        <div className={styles.confirmBody}>{body}</div>
        <div className={styles.confirmActions}>
          <Button ref={cancelRef} onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button className={styles.danger} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
